import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import ModalWrapper from '@components/wrapper/Modal/ModalWrapper';
import Button from '@components/dataEntry/button/Button';
import {
  ModalInner,
  ModalContentsGrid,
  ModalHeader,
} from '@components/wrapper/Modal/ModalStyle';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { useMemberPermission } from '@hooks/usePermission';
import { useInputs } from '@hooks/useInputs';
import TextInput from '@components/dataEntry/Input/TextInput';
import { parchaseItem } from '@store/firebase/store/parchase-item';
import SelectMyCharacter from '@components/dataEntry/select/SelectUsers/SelectMyCharacter';
import { IstoreItem } from '@typings/store/storeItem.interface';
import { useRecoilValue } from 'recoil';
import { currentUserIDState, currentUserWalletState } from '@atom/setting/user';
import { CollectionPath } from '@typings/collections.enum';
import ParchaseConfirmModal from '@pages/Store/Detail/ParchaseConfirmModal';
import ItemInfoSec from '@pages/PhotoCard/PhotoCardDetail/statusContents/inventory/ItemInfoSec';
import { mainPhotocardDataState } from '@atom/setting/photocardAutoSetState';
import {
  canPurchaseStoreItemByCamp,
  getStorePurchaseLimitMessage,
  STORE_NO_CAMP_ID,
} from '@utils/store/storeAccess';
import { useDocument } from '@nandorojo/swr-firestore';
import StoreStockBadge from '@components/dataDisplay/Store/StoreStockBadge';
import { ICampItem } from '@typings/photocard/camp.interface';

const ButtonWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
  min-width: 0;
  max-width: 100%;

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const CharacterSelectRow = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  min-width: 0;
`;

const PurchaseActionRow = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
  flex-wrap: nowrap;

  @media (max-width: 480px) {
    gap: 6px;
  }
`;

const StockInfoColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const LocalModalContentsGrid = styled(ModalContentsGrid)`
  grid-template-rows: auto 1fr;
`;

const LocalModalHeader = styled(ModalHeader)`
  height: auto;
  min-height: 72px;
  align-items: start;
  padding-top: 12px;
  padding-bottom: 12px;

  @media (max-width: 480px) {
    min-height: 72px;
  }
`;

const BlockReason = styled.span`
  width: 100%;
  color: var(--warningColor, var(--primaryColor));
  font-size: 12px;
  text-align: right;
`;

const PurchaseLimitInfo = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  padding: 3px 6px;
  border-radius: 4px;
  border: 1px solid var(--lineColor);
  color: var(--subTextColor);
  background-color: var(--backgroundColor);
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
`;

type Props = {
  data: IstoreItem;
  closeModal: () => void;
};

function DetailModal({ data, closeModal }: Props) {
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  const currentUserWallet = useRecoilValue(currentUserWalletState);
  const representativeCharacter = useRecoilValue(mainPhotocardDataState);
  const representativeCampId = representativeCharacter?.camp || '';

  const [openConfirm, setOpenConfirm] = useState(false);
  const closeConfirmModal = () => {
    closeModal();
    setOpenConfirm(false);
  };
  const currentUserID = useRecoilValue(currentUserIDState);
  const { data: liveStoreItem, loading: storeItemLoading } =
    useDocument<IstoreItem>(
      data?.id ? `${CollectionPath.STORE}/${data.id}` : null,
      { listen: true },
    );
  const storeItemData =
    liveStoreItem?.exists === false ? data : liveStoreItem || data;
  const perUserLimit = Number(storeItemData?.purchaseLimit?.perUser || 0);
  const shouldCheckPerUserLimit = perUserLimit > 0 && Boolean(currentUserID);
  const shouldCheckStoreItem = Boolean(data?.id);
  const { data: userPurchaseCount, loading: userPurchaseCountLoading } =
    useDocument<any>(
      shouldCheckPerUserLimit
        ? `${CollectionPath.STORE}/${data.id}/purchaseUsers/${currentUserID}`
        : null,
      { listen: true },
    );
  const { data: camp1 } = useDocument<ICampItem>(`${CollectionPath.CAMP}/camp1`, {
    listen: true,
  });
  const { data: camp2 } = useDocument<ICampItem>(`${CollectionPath.CAMP}/camp2`, {
    listen: true,
  });
  const { data: camp3 } = useDocument<ICampItem>(`${CollectionPath.CAMP}/camp3`, {
    listen: true,
  });
  const { data: camp4 } = useDocument<ICampItem>(`${CollectionPath.CAMP}/camp4`, {
    listen: true,
  });
  const controlPermission = useMemberPermission({});
  const [{ amount }, handleChange] = useInputs({
    amount: 1,
  });
  const [disabled, setDisabled] = useState(true);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const isPurchasingRef = useRef(false);
  // 지정된 캐릭터가 없거나
  // 현재 소지금으로 살 수 없는 경우(가격 x 수량) 구매 불가
  const unitPrice = Number((data as any)?.price ?? 0);
  const qty = Number(amount ?? 0);
  const totalCost = Math.max(0, unitPrice * qty || 0);
  const wallet = Number(currentUserWallet || 0);
  const invalidAmount = qty <= 0;
  const campBlocked = !canPurchaseStoreItemByCamp(
    storeItemData,
    representativeCampId,
  );
  const isPurchaseLimitLoading =
    (shouldCheckStoreItem && storeItemLoading) ||
    (shouldCheckPerUserLimit && userPurchaseCountLoading);
  const totalLimitMessage = isPurchaseLimitLoading
    ? ''
    : getStorePurchaseLimitMessage(storeItemData, qty);
  const userPurchasedAmount = Number(userPurchaseCount?.amount || 0);
  const perUserLimitBlocked =
    !isPurchaseLimitLoading &&
    perUserLimit > 0 &&
    userPurchasedAmount + qty > perUserLimit;
  const perUserLimitInfo =
    perUserLimit > 0
      ? isPurchaseLimitLoading
        ? '유저당 확인 중'
        : `유저당 ${userPurchasedAmount} / ${perUserLimit}`
      : '';
  const purchaseLimitMessage = isPurchaseLimitLoading
    ? '구매 제한을 확인 중입니다.'
    : perUserLimitBlocked
    ? ''
    : totalLimitMessage;
  const limitBlocked =
    isPurchaseLimitLoading || Boolean(totalLimitMessage) || perUserLimitBlocked;
  const blockBtn =
    !selectedOwnerId ||
    invalidAmount ||
    wallet < totalCost ||
    campBlocked ||
    limitBlocked;
  const campNameMap = useMemo(
    () =>
      new Map(
        [
          { id: STORE_NO_CAMP_ID, title: '진영없음' },
          camp1,
          camp2,
          camp3,
          camp4,
        ]
          .filter((camp): camp is Pick<ICampItem, 'id' | 'title'> =>
            Boolean(camp?.id),
          )
          .map((camp) => [camp.id, camp.title]),
      ),
    [camp1, camp2, camp3, camp4],
  );
  const purchasableCampNames = (storeItemData?.purchasableCampIds || [])
    .map((campId) => campNameMap.get(campId) || campId)
    .filter(Boolean)
    .join(', ');
  const campBlockMessage = `${
    purchasableCampNames || '허용된 진영'
  } 만 구매할 수 있습니다`;
  const blockMessages = [
    !selectedOwnerId ? '아이템을 받을 캐릭터를 선택해주세요.' : '',
    invalidAmount ? '구매 수량은 1개 이상이어야 합니다.' : '',
    wallet < totalCost ? '소지금이 부족합니다.' : '',
    campBlocked ? campBlockMessage : '',
    purchaseLimitMessage,
  ].filter((message): message is string => Boolean(message));
  const blockMessage = blockMessages[0];

  const handleSave = useCallback(async () => {
    if (isPurchasingRef.current) return;

    let parchaseResult = true;
    const unitPrice = Number((data as any)?.price ?? 0);
    const qty = Number(amount ?? 0);
    const total = Math.max(0, unitPrice * qty || 0);
    const myWallet = Number(currentUserWallet || 0);
    if (
      !selectedOwnerId ||
      qty <= 0 ||
      myWallet < total ||
      campBlocked ||
      limitBlocked
    ) {
      alert(
        blockMessage || '구매할 수 없습니다. 수량 또는 소지금을 확인해주세요.',
      );
      return;
    }

    try {
      isPurchasingRef.current = true;
      setIsPurchasing(true);
      await parchaseItem({
        collectionPath: `${CollectionPath.PHOTOCARD}`,
        photocardID: selectedOwnerId,
        data: { ...storeItemData, amount },
        amount,
        buyerID: currentUserID || '', //계정주 지갑에서 돈빼감
        purchaserCampId: representativeCampId,
        enforceStorePurchaseLimit: true,
      });

      setOpenConfirm(parchaseResult);
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : '구매 처리 중 오류가 발생했습니다.';
      alert(message);
    } finally {
      isPurchasingRef.current = false;
      setIsPurchasing(false);
    }
  }, [
    storeItemData,
    selectedOwnerId,
    currentUserID,
    amount,
    currentUserWallet,
    campBlocked,
    limitBlocked,
    blockMessage,
    representativeCampId,
  ]);

  useEffect(() => {
    if (!selectedOwnerId && representativeCharacter?.id) {
      setSelectedOwnerId(representativeCharacter.id);
    }
  }, [selectedOwnerId, representativeCharacter?.id]);

  useEffect(() => {
    setDisabled(blockBtn || isPurchasing);
  }, [blockBtn, isPurchasing]);

  if (!data) return null;

  return (
    <>
      <ModalWrapper
        outsideClick={closeConfirmModal}
        contentStyle={{
          width: 'min(460px, 100vw)',
          minWidth: 'min(460px, 100vw)',
          maxWidth: 'min(460px, 96vw)',
        }}
      >
        <LocalModalContentsGrid>
          <LocalModalHeader>
            <CloseBtn onClick={closeModal} shape="stroke" size="sm" />
            <div />
            {controlPermission && (
              <ButtonWrap>
                <CharacterSelectRow>
                  <SelectMyCharacter
                    selectedOwnerId={selectedOwnerId}
                    setSelectedOwnerId={setSelectedOwnerId}
                    label="캐릭터"
                    style={{ width: 'fit-content' }}
                  />
                </CharacterSelectRow>
                <PurchaseActionRow>
                  <TextInput
                    value={amount}
                    name="amount"
                    onChange={handleChange}
                    type="number"
                    placeholder="1"
                    style={{ width: '60px' }}
                    min={0}
                  />
                  <StockInfoColumn>
                    <StoreStockBadge data={storeItemData} />
                    {perUserLimitInfo && (
                      <PurchaseLimitInfo>{perUserLimitInfo}</PurchaseLimitInfo>
                    )}
                  </StockInfoColumn>
                  <Button
                    onClick={handleSave}
                    disabled={disabled}
                    pending={isPurchasing}
                  >
                    {isPurchasing ? '구매 중' : blockBtn ? '구매불가' : '구매'}
                  </Button>
                </PurchaseActionRow>
                {blockBtn &&
                  blockMessages.map((message) => (
                    <BlockReason key={message}>{message}</BlockReason>
                  ))}
              </ButtonWrap>
            )}
          </LocalModalHeader>
          <ModalInner>
            <ItemInfoSec data={storeItemData} showWallet amount={amount} />
          </ModalInner>
        </LocalModalContentsGrid>
      </ModalWrapper>

      {openConfirm && (
        <ParchaseConfirmModal closeModal={closeConfirmModal} data={data} />
      )}
    </>
  );
}
export default DetailModal;
